import type React from "react";
import { Stack } from "@mantine/core";
import { Table } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ReviewStatus } from "@models/job";
import type { RowSaveStatus } from "@hooks/useJobResults";
import RowSaveIndicator from "@components/ResultRowCard/RowSaveIndicator";
import StatusBadge from "./StatusBadge";
import { REVIEW_STATUS_BADGE } from "@utils/resultStatusBadges";

interface RowStatusCellProps {
  status: ReviewStatus;
  saveState: RowSaveStatus | undefined;
  onRetrySave: () => void;
}

/**
 * The Status column: ONE axis, what a REVIEWER did.
 *
 * It used to carry a second badge for what the pipeline concluded. Both tables
 * moved that one under the suggestion it judges (see `RowFindingBadge`) — a
 * finding is a claim about one cell's contents, and two cells away it read as a
 * claim about the whole row.
 *
 * The badge was an icon. A glyph has to be decoded before it can be read, and
 * this table is scanned in bulk by someone deciding what to accept — the word
 * is the point, and it keeps its full explanation on hover.
 */
const RowStatusCell: React.FC<RowStatusCellProps> = ({
  status,
  saveState,
  onRetrySave,
}) => {
  const { t } = useTranslation();
  const review = REVIEW_STATUS_BADGE[status];

  return (
    <Table.Td>
      <Stack gap={4} align="flex-start">
        <StatusBadge
          spec={review}
          text={t(review.badgeKey, { defaultValue: review.badge })}
          label={t(review.labelKey, { defaultValue: review.fallback })}
        />
        <RowSaveIndicator saveState={saveState} onRetrySave={onRetrySave} />
      </Stack>
    </Table.Td>
  );
};

export default RowStatusCell;
